import { AchievementType } from '@vae/server/dist/client'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import useUser from '../../../contexts/hooks/useUser'
import { useAchievementRefresh } from '../../../hooks/useAchievementRefresh'
import { trpc } from '../../../modules/trpc'
import { generateChartData } from '../utils/chart/generateChartData'

const typeOrder: AchievementType[] = [
	'consumptionEffect',
	'priceTrackers',
	'becomeOurCustomer',
	'connectDevice',
	'smartCharging',
	'unknown',
]

export type AchievementsHook = ReturnType<typeof useAchievements>

export const useAchievements = () => {
	const utils = trpc.useContext()

	const { user } = useUser()

	const { refreshAchievements: globalRefreshAchievements } = useAchievementRefresh()

	if (!user) {
		return {
			achievements: [],
			chartData: [],
			currentMonthPoints: null,
			mutateAchievements: () => Promise.resolve(),
			setPointsAwarded: () => Promise.resolve(),
			setLastObservedStep: () => Promise.resolve(),
			refreshAchievements: () => Promise.resolve(),
			isLoading: false,
		}
	}

	const { data, isInitialLoading, refetch } = trpc.achievements.getUserAchievements.useQuery(
		user.id,
		{
			refetchInterval: 30000,
			refetchOnWindowFocus: true,
			staleTime: 25000,
		}
	)
	const { mutate: mutateAchievements } = trpc.achievements.editUserAchievement.useMutation({
		onSuccess: () => {
			utils.achievements.getUserAchievements.invalidate(user.id)
			utils.achievements.getUserCurrentLevel.invalidate({ userId: user.id })
			utils.achievements.getUserTotalPoints.invalidate({ userId: user.id })
			utils.achievements.getUserPercentage.invalidate({ userId: user.id })
		},
	})

	const refreshAchievements = async () => {
		await globalRefreshAchievements()
		await refetch()
	}

	const setPointsAwarded = (achievementId: number) => {
		mutateAchievements({
			userAchievementId: achievementId,
			data: { pointsAwarded: true },
		})
	}

	const setLastObservedStep = (achievementId: number, step: number) => {
		mutateAchievements({
			userAchievementId: achievementId,
			data: { lastObservedStep: step },
		})
	}

	const achievements = useMemo(
		() =>
			(data?.result || []).map(a => ({
				...a,
				completedAt: a.completedAt ? dayjs(a.completedAt).toDate() : null,
				createdAt: a.createdAt ? dayjs(a.createdAt).toDate() : new Date(0),
			})),
		[data?.result]
	)

	const { achievementsByType, currentMonthPoints } = useMemo(() => {
		const byType = achievements.reduce<Partial<Record<AchievementType, typeof achievements>>>(
			(acc, a) => {
				const type = a.achievement.type
				if (!acc[type]) acc[type] = []
				acc[type].push(a)
				return acc
			},
			{}
		)

		Object.values(byType).forEach(list => list!.sort((a, b) => a.id - b.id))

		const sortedAchievementsByType = typeOrder.reduce<Record<AchievementType, typeof achievements>>(
			(acc, type) => {
				if (byType[type]) acc[type] = byType[type]!
				return acc
			},
			{} as Record<AchievementType, typeof achievements>
		)

		const points = isInitialLoading
			? null
			: achievements.reduce((total, achievement) => {
					const { completedAt, isCompleted, pointsAwarded } = achievement

					const applicablePoints = completedAt !== null && isCompleted && pointsAwarded
					const completedThisMonth = dayjs(completedAt).isSame(dayjs(), 'month')

					if (applicablePoints && completedThisMonth) {
						return total + achievement.achievement.points
					}

					return total
				}, 0)

		return {
			achievementsByType: sortedAchievementsByType,
			currentMonthPoints: points,
		}
	}, [achievements, isInitialLoading])

	const chartData = useMemo(() => generateChartData(achievements), [achievements])

	return {
		achievements,
		chartData,
		achievementsByType,
		setPointsAwarded,
		setLastObservedStep,
		refreshAchievements,
		isLoading: isInitialLoading,
		currentMonthPoints,
	}
}
